import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {any} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {any} */
  #embla = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.presentation === 'carousel') {
      // Defer to ensure Embla library and child component refs are ready
      requestAnimationFrame(() => this.#initEmbla());
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }

    this.#controller.abort();
  }

  #initEmbla() {
    const slideshow = /** @type {any} */ (this.refs.slideshow);
    if (!slideshow) {
      return;
    }

    const container = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-container'));
    const slidesContainer = /** @type {HTMLElement | null} */ (container?.querySelector('slideshow-slides'));

    if (!container || !slidesContainer) {
      return;
    }

    // Disable the old slideshow completely
    slideshow.setAttribute('disabled', 'true');
    if (slideshow.scroller) {
      slideshow.scroller = null;
    }

    // Ensure slideshow-slides is first child (Embla grabs first child as container)
    if (container.firstElementChild !== slidesContainer) {
      container.prepend(slidesContainer);
    }

    // Apply viewport styles via JS (highest specificity, matches carousel-navigation-embla.js)
    container.style.overflowX = 'clip';
    container.style.overflowY = 'visible';

    // Apply slides container styles via JS
    slidesContainer.style.overflow = 'visible';
    slidesContainer.style.scrollSnapType = 'none';
    slidesContainer.style.scrollBehavior = 'auto';
    slidesContainer.style.gap = '0';
    slidesContainer.style.cursor = 'grab';
    slidesContainer.style.willChange = 'transform';
    slidesContainer.style.backfaceVisibility = 'hidden';

    // Ensure all slides are visible for Embla measurement
    const slideEls = /** @type {HTMLElement[]} */ (Array.from(slidesContainer.querySelectorAll('slideshow-slide')));
    slideEls.forEach((/** @type {HTMLElement} */ slide) => {
      slide.style.contentVisibility = 'visible';
    });

    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (!EmblaCarousel) {
      return;
    }

    // Set up accessibility plugin if available
    const plugins = [];
    try {
      const EmblaCarouselAccessibility = /** @type {any} */ (window).EmblaCarouselAccessibility;
      if (typeof EmblaCarouselAccessibility === 'function') {
        plugins.push(EmblaCarouselAccessibility({ active: true }));
      }
    } catch (_) {
      // Accessibility plugin not available
    }

    // Reuse headless Embla instance created by slideshow-embla-init.js if present
    if (slideshow._embla) {
      slideshow._embla.destroy();
      slideshow._embla = null;
    }

    this.#embla = EmblaCarousel(container, {
      loop: true,
      align: 'start',
      duration: 25,
    }, plugins);


    this.#bridgeControls(slideshow);

    // Sync slide state on Embla select
    this.#embla.on('select', () => {
      this.#syncSlideState(slideshow);
    });

    // Wire zoom clicks on slides — only open if Embla says click is allowed (not a drag)
    this.#wireZoomClicks(slideshow);

    // Initial sync
    this.#syncSlideState(slideshow);
  }

  /** @param {any} slideshow */
  #bridgeControls(slideshow) {
    const embla = this.#embla;
    if (!embla) return;

    const { signal } = this.#controller;

    // Wire arrow buttons directly to Embla (stopPropagation prevents
    // the Component framework's on:click handler from also firing)
    const prevBtn = /** @type {HTMLElement | null} */ (slideshow.querySelector('[ref="previous"]'));
    const nextBtn = /** @type {HTMLElement | null} */ (slideshow.querySelector('[ref="next"]'));

    if (prevBtn) {
      prevBtn.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        embla.goToPrev();
      }, { signal });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        embla.goToNext();
      }, { signal });
    }


    // Wire thumbnail clicks directly to Embla (query DOM directly)
    const thumbBtns = /** @type {NodeListOf<HTMLElement>} */ (slideshow.querySelectorAll('[ref="thumbnails[]"]'));
    thumbBtns.forEach((/** @type {HTMLElement} */ thumb, /** @type {number} */ i) => {
      thumb.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        embla.goTo(i);
      }, { signal });
    });


    // Wire dot clicks directly to Embla
    const dotBtns = /** @type {NodeListOf<HTMLElement>} */ (slideshow.querySelectorAll('[ref="dots[]"]'));
    dotBtns.forEach((/** @type {HTMLElement} */ dot, /** @type {number} */ i) => {
      dot.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        embla.goTo(i);
      }, { signal });
    });

    if (dotBtns.length) {
    }
  }

  /** @param {any} slideshow */
  #wireZoomClicks(slideshow) {
    const embla = this.#embla;
    if (!embla) return;

    const { signal } = this.#controller;
    const slides = /** @type {NodeListOf<HTMLElement>} */ (slideshow.querySelectorAll('slideshow-slide[data-zoom-index]'));

    slides.forEach((/** @type {HTMLElement} */ slide) => {
      slide.addEventListener('click', (/** @type {MouseEvent} */ e) => {
        if (typeof embla.clickAllowed === 'function' && !embla.clickAllowed()) return;
        const index = parseInt(slide.dataset.zoomIndex ?? '0', 10);
        this.zoom(index, /** @type {PointerEvent} */ (e));
      }, { signal });
    });
  }

  /** @param {any} slideshow */
  #syncSlideState(slideshow) {
    const embla = this.#embla;
    if (!embla) return;

    const index = embla.selectedSnap();

    // Update slideshow current (syncs dots, thumbnails, counter, button states)
    slideshow.current = index;

    // Update aria-hidden on slides
    const slides = /** @type {NodeListOf<HTMLElement>} */ (slideshow.querySelectorAll('slideshow-slide'));
    slides.forEach((/** @type {HTMLElement} */ slide, /** @type {number} */ i) => {
      slide.setAttribute('aria-hidden', String(i !== index));
    });

    // Center selected thumbnail
    const thumbs = /** @type {NodeListOf<HTMLElement>} */ (slideshow.querySelectorAll('[ref="thumbnails[]"]'));
    const thumb = thumbs[index];
    if (thumb) {
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.#embla) {
      this.#embla.goTo(event.detail.index);
    } else {
      this.slideshow?.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
